import { useCallback, useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from '@chakra-ui/react';
import { LocalTrackPublication, Track } from 'livekit-client';
import { LiveKitConfig } from '../livekit/config';
import { useLiveKit } from '../livekit/hooks/useLiveKit';
import { StreamingPiP } from './StreamingPiP';

export interface LiveKitVideoProps {
  roomName: string;
  participantName: string;
  mode: 'stream' | 'join';
  /** Show PiP automatically on connect */
  autoShowPiP?: boolean;
  /** Custom PiP position */
  pipPosition?: { x: number; y: number };
  /** Viewer theme for different display modes */
  viewerTheme?: 'normal' | 'mini' | 'icon';
  /** Callback when connection state changes */
  onStateChange?: (state: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  /** Callback when video starts/stops */
  onVideoChange?: (hasVideo: boolean) => void;
}

/**
 * Simple LiveKit Video Component
 *
 * All-in-one component for LiveKit video streaming with PiP.
 * Just provide room/participant info and mode - everything else is handled automatically.
 *
 * @example
 * // Stream video
 * <LiveKitVideo
 *   roomName="my-room"
 *   participantName="streamer-1"
 *   mode="stream"
 *   autoShowPiP
 * />
 *
 * // View video
 * <LiveKitVideo
 *   roomName="my-room"
 *   participantName="viewer-1"
 *   mode="join"
 *   autoShowPiP
 * />
 */
export const LiveKitVideo: React.FC<LiveKitVideoProps> = ({
  roomName,
  participantName,
  mode,
  autoShowPiP = true,
  viewerTheme = 'normal',
  onStateChange,
  onVideoChange,
}) => {
  const [showPiP, setShowPiP] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);

  // Debug logs
  console.log('🔍 LiveKitVideo Debug:', {
    roomName,
    participantName,
    mode,
    autoShowPiP,
    viewerTheme,
    showPiP,
    hasVideo,
  });

  // Check if LiveKit is properly configured
  const configValidation = LiveKitConfig.validateConfig();
  const isLiveKitConfigured = configValidation.isValid;

  console.log('🔍 LiveKit Config:', { isLiveKitConfigured, errors: configValidation.errors });

  // LiveKit connection hook
  const { startStreaming, joinAsViewer, state, room, currentMode, isConnected } = useLiveKit({
    onStateChange: (newState) => {
      console.log('🔍 LiveKit State Change:', newState);
      onStateChange?.(newState);

      // Auto-show PiP when connected
      if (newState === 'connected' && autoShowPiP) {
        console.log('🔍 Auto-showing PiP on connect');
        setShowPiP(true);
      }

      // Reset video state on disconnect
      if (newState === 'disconnected') {
        console.log('🔍 Resetting video state on disconnect');
        setHasVideo(false);
        setShowPiP(false);
      }
    },
  });

  console.log('🔍 LiveKit Hook State:', { state, isConnected, currentMode, hasRoom: !!room });

  // Auto-connect based on mode
  useEffect(() => {
    if (!isLiveKitConfigured) {
      console.warn('LiveKit not configured - video streaming will not work');
      return;
    }

    if (state === 'idle') {
      const connect = async () => {
        try {
          if (mode === 'stream') {
            await startStreaming(roomName, participantName);
          } else {
            await joinAsViewer(roomName, participantName);
          }
        } catch (error) {
          console.error('❌ Failed to connect:', error);
        }
      };
      connect();
    }
  }, [roomName, participantName, mode, startStreaming, joinAsViewer, state, isLiveKitConfigured]);

  // Handle video track attachment
  useEffect(() => {
    if (!room || !isConnected) return;

    const attachTrack = (track: Track) => {
      if (track.kind === Track.Kind.Video) {
        try {
          setHasVideo(true);
          onVideoChange?.(true);
          console.log(`✅ Video track available (${mode} mode)`);
        } catch (error) {
          console.error('❌ Failed to handle video track:', error);
        }
      }
    };

    // Listen for new tracks
    const handleTrackSubscribed = (track: Track) => {
      if (mode === 'join') attachTrack(track);
    };

    const handleLocalTrackPublished = (publication: LocalTrackPublication) => {
      if (mode === 'stream' && publication.track) {
        attachTrack(publication.track);
      }
    };

    // Set up event listeners
    room.on('trackSubscribed', handleTrackSubscribed);
    room.on('localTrackPublished', handleLocalTrackPublished);

    // Check for existing tracks
    if (mode === 'stream' && room.localParticipant) {
      room.localParticipant.videoTrackPublications.forEach((publication) => {
        if (publication.track) attachTrack(publication.track);
      });
    } else if (mode === 'join' && room.remoteParticipants) {
      room.remoteParticipants.forEach((participant) => {
        participant.videoTrackPublications.forEach((publication) => {
          if (publication.track) attachTrack(publication.track);
        });
      });
    }

    // Cleanup
    return () => {
      room.off('trackSubscribed', handleTrackSubscribed);
      room.off('localTrackPublished', handleLocalTrackPublished);
    };
  }, [room, onVideoChange, mode, isConnected]);

  // Setup PiP events for track attachment
  const handlePiPSetupEvents = useCallback(
    (_room: any, videoElement: HTMLVideoElement, mode: 'stream' | 'join') => {
      if (!room) return () => {};

      const attachTrack = (track: Track) => {
        if (track.kind === Track.Kind.Video && videoElement) {
          try {
            track.attach(videoElement);
            setHasVideo(true);
            onVideoChange?.(true);
            console.log(`✅ Video track attached to PiP (${mode} mode)`);
          } catch (error) {
            console.error('❌ Failed to attach video track to PiP:', error);
          }
        }
      };

      // Listen for new tracks
      const handleTrackSubscribed = (track: Track) => {
        if (mode === 'join') attachTrack(track);
      };

      const handleLocalTrackPublished = (publication: LocalTrackPublication) => {
        if (mode === 'stream' && publication.track) {
          attachTrack(publication.track);
        }
      };

      // Set up event listeners
      room.on('trackSubscribed', handleTrackSubscribed);
      room.on('localTrackPublished', handleLocalTrackPublished);

      // Check for existing tracks
      if (mode === 'stream' && room.localParticipant) {
        room.localParticipant.videoTrackPublications.forEach((publication) => {
          if (publication.track) attachTrack(publication.track);
        });
      } else if (mode === 'join' && room.remoteParticipants) {
        room.remoteParticipants.forEach((participant) => {
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track) attachTrack(publication.track);
          });
        });
      }

      // Cleanup
      return () => {
        room.off('trackSubscribed', handleTrackSubscribed);
        room.off('localTrackPublished', handleLocalTrackPublished);
      };
    },
    [room, onVideoChange]
  );

  // Show configuration error if LiveKit is not configured
  if (!isLiveKitConfigured) {
    return (
      <Box position="fixed" top={4} right={4} zIndex={1000} maxW="400px">
        <Alert status="warning" borderRadius="md" boxShadow="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>LiveKit Not Configured</AlertTitle>
            <AlertDescription>
              Video streaming requires LiveKit configuration. Please set up your environment
              variables:
              <br />
              <code>VITE_LIVEKIT_URL</code>, <code>VITE_LIVEKIT_API_KEY</code>,{' '}
              <code>VITE_LIVEKIT_API_SECRET</code>
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Don't render anything if not connected or PiP disabled
  if (!room || !showPiP || !isConnected) {
    return null;
  }

  return (
    <StreamingPiP
      room={{
        name: room.name,
        localParticipant: room.localParticipant
          ? { identity: room.localParticipant.identity }
          : undefined,
        remoteParticipants: room.remoteParticipants,
      }}
      isVisible={showPiP}
      onClose={() => setShowPiP(false)}
      mode={currentMode || mode}
      hasVideo={hasVideo}
      viewerTheme={viewerTheme === 'icon' ? 'normal' : viewerTheme}
      onSetupEvents={handlePiPSetupEvents}
    />
  );
};

// Export hook for advanced usage
export { useLiveKit } from '../livekit/hooks/useLiveKit';
